/**
 * iter318 wave 15 — VyomOS Demo lead capture + verified session (Phase D).
 *
 * Flow:
 *   1. Visitor fills form → POST /api/public/demo/request → receives token.
 *      (Emailing is MOCKED for now — the token is shown inline so the flow
 *      works end-to-end. Production wave replaces this with an email link.)
 *   2. Visitor pastes the token → POST /api/public/demo/verify → gets a
 *      30-minute session with a countdown.
 *   3. Session view shows remaining time and a link to launch the demo
 *      environment (future wave will build the actual demo tenant).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Sparkles, Timer, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VyomosDemo() {
  const [step, setStep] = useState('request'); // 'request' | 'verify' | 'session'
  const [lead, setLead] = useState(null);
  const [session, setSession] = useState(null);

  return (
    <div data-testid="vyomos-demo">
      <section className="bg-gradient-to-b from-[#EAF5FF] to-[#F8FBFF]">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#DCEEFF] text-[#1677FF] text-xs font-medium mb-4">
            <Sparkles className="h-3 w-3" /> 30-minute interactive demo
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">
            Experience VyomOS in a live environment.
          </h1>
          <p className="text-lg text-[#566579] max-w-2xl mx-auto">
            No sales conversation required. Verify your email and get a full 30-minute
            hands-on session with realistic sample data.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-3xl mx-auto px-6 py-12">
          <Stepper step={step} />
          {step === 'request' && (
            <RequestForm
              onSuccess={(l) => { setLead(l); setStep('verify'); }}
            />
          )}
          {step === 'verify' && (
            <VerifyForm
              lead={lead}
              onSuccess={(s) => { setSession(s); setStep('session'); }}
              onBack={() => setStep('request')}
            />
          )}
          {step === 'session' && (
            <SessionView session={session} />
          )}
        </div>
      </section>
    </div>
  );
}

function Stepper({ step }) {
  const items = [
    { key: 'request', label: 'Details' },
    { key: 'verify', label: 'Verify' },
    { key: 'session', label: 'Launch' },
  ];
  const idx = items.findIndex((i) => i.key === step);
  return (
    <ol className="flex items-center justify-center gap-2 mb-10 text-sm" data-testid="demo-stepper">
      {items.map((it, i) => (
        <li key={it.key} className="flex items-center gap-2">
          <div className={
            i < idx ? 'h-7 w-7 rounded-full bg-[#1677FF] text-white flex items-center justify-center text-xs font-semibold'
              : i === idx ? 'h-7 w-7 rounded-full bg-[#1677FF] text-white flex items-center justify-center text-xs font-semibold ring-4 ring-[#EAF5FF]'
                : 'h-7 w-7 rounded-full bg-white border border-[#DCE6F0] text-[#566579] flex items-center justify-center text-xs font-semibold'
          }>
            {i < idx ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
          </div>
          <span className={i === idx ? 'font-semibold text-[#10233F]' : 'text-[#566579]'}>{it.label}</span>
          {i < items.length - 1 && <span className="w-8 h-px bg-[#DCE6F0] mx-1" />}
        </li>
      ))}
    </ol>
  );
}

function RequestForm({ onSuccess }) {
  const [form, setForm] = useState({ name: '', email: '', organization: '', role: '', participants: '', country: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/public/demo/request`, form);
      onSuccess(data);
      toast.success('Verification token issued.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request demo');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#DCE6F0] rounded-3xl p-8 shadow-sm space-y-4" data-testid="demo-request-form">
      <h2 className="text-xl font-bold mb-1">Tell us a bit about you</h2>
      <p className="text-sm text-[#566579] mb-4">We tailor the demo data to your context.</p>
      <div className="grid md:grid-cols-2 gap-3">
        <Field label="Your name" required>
          <input required value={form.name} onChange={set('name')} data-testid="demo-name" className="demo-input" />
        </Field>
        <Field label="Work email" required>
          <input required type="email" value={form.email} onChange={set('email')} data-testid="demo-email" className="demo-input" />
        </Field>
        <Field label="Organization">
          <input value={form.organization} onChange={set('organization')} data-testid="demo-org" className="demo-input" />
        </Field>
        <Field label="Your role">
          <input value={form.role} onChange={set('role')} data-testid="demo-role" className="demo-input" placeholder="Owner, Ops, Coach…" />
        </Field>
        <Field label="Active participants">
          <select value={form.participants} onChange={set('participants')} data-testid="demo-participants" className="demo-input">
            <option value="">Select…</option>
            <option>Under 100</option><option>100–500</option><option>500–2,000</option><option>2,000+</option>
          </select>
        </Field>
        <Field label="Country">
          <input value={form.country} onChange={set('country')} data-testid="demo-country" className="demo-input" />
        </Field>
      </div>
      <Field label="Anything specific you want to see?">
        <textarea rows={3} value={form.notes} onChange={set('notes')} data-testid="demo-notes" className="demo-input" />
      </Field>
      <button
        type="submit" disabled={busy}
        data-testid="demo-request-submit"
        className="mt-2 px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] disabled:opacity-60 text-white font-semibold transition"
      >
        {busy ? 'Sending…' : 'Continue →'}
      </button>
      <p className="text-xs text-[#566579]">By continuing you agree to our privacy policy. Your demo data is isolated and cleared after the session ends.</p>
      <style>{`
        .demo-input { width:100%; padding:10px 14px; border:1px solid #DCE6F0; border-radius:12px; font-size:14px; background:#fff; color:#10233F; transition: border-color .15s ease; }
        .demo-input:focus { outline:none; border-color:#1677FF; box-shadow: 0 0 0 3px rgba(22,119,255,.12); }
      `}</style>
    </form>
  );
}

function VerifyForm({ lead, onSuccess, onBack }) {
  const [token, setToken] = useState(lead?.verify_token || '');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/public/demo/verify`, {
        email: lead.email, token,
      });
      onSuccess(data);
      toast.success('Verified — session started.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white border border-[#DCE6F0] rounded-3xl p-8 shadow-sm" data-testid="demo-verify-form">
      <h2 className="text-xl font-bold mb-1">Check your email</h2>
      <p className="text-sm text-[#566579] mb-6">
        We&apos;ve sent a verification link to <strong>{lead?.email}</strong>. Enter the token from that email to launch your session.
      </p>

      <div className="bg-[#FFF9E6] border border-[#F1D97A] rounded-2xl p-4 mb-6 text-sm" data-testid="demo-mock-notice">
        <div className="font-semibold text-[#7A6110] mb-1">Demo build — email delivery is stubbed</div>
        <p className="text-[#7A6110] leading-relaxed">
          In production a verification email would be sent. For now the token is pre-filled below so you can complete the flow.
        </p>
      </div>

      <label className="block text-xs font-semibold uppercase tracking-wider text-[#10233F] mb-1">Verification token</label>
      <input
        value={token} onChange={(e) => setToken(e.target.value)}
        required
        data-testid="demo-verify-token"
        className="w-full px-3 py-2 border border-[#DCE6F0] rounded-lg font-mono text-sm mb-4"
      />
      <div className="flex gap-2">
        <button type="button" onClick={onBack} className="px-4 py-2 rounded-full text-[#566579] hover:text-[#10233F] text-sm">← Back</button>
        <button
          type="submit" disabled={busy || !token}
          data-testid="demo-verify-submit"
          className="px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] disabled:opacity-60 text-white font-semibold text-sm transition"
        >
          {busy ? 'Verifying…' : 'Verify and launch demo'}
        </button>
      </div>
    </form>
  );
}

function SessionView({ session }) {
  const [remaining, setRemaining] = useState(() => {
    const exp = new Date(session.expires_at).getTime();
    return Math.max(0, Math.floor((exp - Date.now()) / 1000));
  });

  useEffect(() => {
    const iv = setInterval(() => {
      const exp = new Date(session.expires_at).getTime();
      setRemaining(Math.max(0, Math.floor((exp - Date.now()) / 1000)));
    }, 1000);
    return () => clearInterval(iv);
  }, [session.expires_at]);

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');
  const expired = remaining <= 0;

  return (
    <div className="bg-white border border-[#DCE6F0] rounded-3xl p-10 shadow-sm text-center" data-testid="demo-session-view">
      <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mb-4">
        <CheckCircle2 className="h-7 w-7 text-emerald-600" />
      </div>
      <h2 className="text-2xl font-bold mb-1" data-testid="demo-session-hello">Welcome, {session.name.split(' ')[0]}.</h2>
      <p className="text-[#566579] mb-8">Your VyomOS demo session is active. Explore freely.</p>

      <div className="inline-flex items-center gap-2 bg-[#EAF5FF] border border-[#DCEEFF] px-4 py-2 rounded-full mb-8" data-testid="demo-session-timer">
        <Timer className="h-4 w-4 text-[#1677FF]" />
        <span className="font-mono font-semibold text-[#10233F]">{expired ? 'Expired' : `${mm}:${ss} remaining`}</span>
      </div>

      <div className="mb-8 text-left bg-[#F8FBFF] border border-[#DCE6F0] rounded-2xl p-5 max-w-md mx-auto">
        <div className="text-xs uppercase tracking-wider text-[#566579] mb-1">Session ID</div>
        <div className="font-mono text-sm break-all text-[#10233F]" data-testid="demo-session-id">{session.session_id}</div>
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        <a
          href={`https://demo.vyomos.io?session=${encodeURIComponent(session.session_id)}`}
          className={expired ? 'px-6 py-3 rounded-full bg-slate-200 text-slate-500 cursor-not-allowed' : 'px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold'}
          onClick={(e) => { if (expired) e.preventDefault(); }}
          data-testid="demo-launch"
        >
          Launch demo environment →
        </a>
        <a href="/contact" className="px-6 py-3 rounded-full border border-[#DCE6F0] text-[#10233F] hover:border-[#55AEEF]">
          Talk to sales
        </a>
      </div>

      <p className="text-xs text-[#566579] mt-6">Demo environments are isolated. Data resets after the session ends.</p>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold uppercase tracking-wider text-[#10233F] mb-1">
        {label} {required && <span className="text-[#1677FF]">*</span>}
      </label>
      {children}
    </div>
  );
}
